import React, { useState, cloneElement, Component } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { CalendarIcon, MessageCircleIcon, SparklesIcon, FlameIcon } from 'lucide-react';
export function CoachingPage() {
  const handleRequestSession = (tier: string) => {
    const subject = encodeURIComponent(`Coaching Request - ${tier}`);
    const body = encodeURIComponent(`Hi Mandy!\n\nI'm interested in the ${tier} coaching option.\n\nPlease reach out so we can discuss details and get started!\n\nThank you!`);
    window.location.href = `mailto:trippinwithmandy@gmail.com?subject=${subject}&body=${body}`;
  };
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-299">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-blush-50 to-white" data-id="element-300">
        <SparkleEffect count={80} data-id="element-301" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-302">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-303">
            1:1 Coaching
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-304">
            Real-life guidance, mindset support, and confidence rebuilding—not
            therapy, but practical help navigating real-life challenges.
          </motion.p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-306">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-307">
            <div className="aspect-square rounded-[3rem] bg-lavender-100 p-8 flex items-center justify-center relative overflow-hidden border border-lavender-200" data-id="element-308">
              <SparkleEffect count={40} data-id="element-309" />
              <div className="text-center z-10" data-id="element-310">
                <FlameIcon className="w-16 h-16 text-cta-500 mx-auto mb-4" data-id="element-311" />
                <h3 className="font-heading text-3xl text-plum-700 font-bold" data-id="element-312">
                  Who It's For
                </h3>
                <ul className="mt-6 space-y-3 text-left" data-id="element-313">
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-314">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-315" />{' '}
                    You're stuck and you know it.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-316">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-317" />{' '}
                    You're tired of just surviving.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-318">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-319" />{' '}
                    You need someone who won't judge your mess.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-320">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-321" />{' '}
                    You're ready to do the damn work.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-322">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-700 mb-6" data-id="element-323">
              What to Expect
            </h2>
            <ul className="space-y-6" data-id="element-324">
              {[{
              title: 'Real talk, not textbook advice',
              desc: "I don't do fluff. We're going to look at your life honestly and figure out exactly what needs to change."
            }, {
              title: 'Someone who actually gives a damn',
              desc: 'When you work with me, I am fully invested in your healing and your success.'
            }, {
              title: 'Accountability with love (and tough love)',
              desc: 'I will hold your hand, but I will also call you out on your excuses. Because you deserve better.'
            }, {
              title: 'Mindset shifts that stick',
              desc: "We aren't just putting a band-aid on it. We are rewiring how you think and operate."
            }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-325">
                  <div className="mt-1 w-2 h-2 rounded-full bg-cta-500 shrink-0" data-id="element-326" />
                  <div data-id="element-327">
                    <h4 className="font-bold text-plum-700 text-lg" data-id="element-328">
                      {item.title}
                    </h4>
                    <p className="text-plum-500 font-medium" data-id="element-329">{item.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-blush-50 mt-12" data-id="element-330">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-331">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-332">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-333">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-334" />

            {[{
            icon: <CalendarIcon data-id="element-335" />,
            title: '1. Book a Call',
            desc: "Schedule a free discovery call. Let's see if our vibes match."
          }, {
            icon: <MessageCircleIcon data-id="element-336" />,
            title: '2. Get Real',
            desc: 'We map out exactly where you are and where you want to be.'
          }, {
            icon: <SparklesIcon data-id="element-337" />,
            title: '3. Transform',
            desc: 'We do the work. You stop surviving and start living like a badass.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-338">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-339">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-340">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-341">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-white relative overflow-hidden" data-id="element-342">
        <SparkleEffect count={50} data-id="element-343" />
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-344">
          <div className="text-center mb-16" data-id="element-345">
            <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-346">
              Accessible pricing because everyone deserves support.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-347">
            {/* Card 1 */}
            <div className="bg-white border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-348">
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-2" data-id="element-349">
                1:1 Session
              </h3>
              <p className="text-plum-500 font-medium mb-6" data-id="element-350">45–60 minutes</p>
              <div className="mb-8" data-id="element-351">
                <span className="font-heading text-4xl font-bold text-plum-700" data-id="element-352">
                  $25–$45
                </span>
              </div>
              <p className="text-plum-600 font-medium mb-10 flex-grow" data-id="element-353">
                A single deep-dive session to tackle what's on your mind right
                now.
              </p>
              <button onClick={() => handleRequestSession('1:1 Session ($25–$45)')} className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors" data-id="element-354">
                Request a Session
              </button>
            </div>

            {/* Card 2 (Most Popular) */}
            <div className="bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 shadow-2xl flex flex-col relative overflow-hidden transform md:-translate-y-4" data-id="element-355">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cta-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-356" />
              <div className="inline-flex self-start px-4 py-1.5 rounded-full bg-cta-500 text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-357">
                Most Popular
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2" data-id="element-358">
                3 Session Package
              </h3>
              <div className="mb-8 mt-6" data-id="element-359">
                <span className="font-heading text-4xl font-bold text-white" data-id="element-360">
                  $60–$100
                </span>
              </div>
              <p className="text-plum-100 font-medium mb-10 flex-grow" data-id="element-361">
                Three sessions to build momentum and create real, lasting
                change.
              </p>
              <button onClick={() => handleRequestSession('3 Session Package ($60–$100)')} className="w-full py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/30" data-id="element-362">
                Start My Transformation
              </button>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-363">
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-2" data-id="element-364">
                Monthly Support
              </h3>
              <div className="mb-8 mt-12" data-id="element-365">
                <span className="font-heading text-4xl font-bold text-plum-700" data-id="element-366">
                  $120–$180
                </span>
                <span className="text-plum-500 font-medium" data-id="element-367">/month</span>
              </div>
              <p className="text-plum-600 font-medium mb-10 flex-grow" data-id="element-368">
                Ongoing support with weekly check-ins to keep you on track and
                thriving.
              </p>
              <button onClick={() => handleRequestSession('Monthly Support ($120–$180/mo)')} className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors" data-id="element-369">
                Apply for Monthly Support
              </button>
            </div>
          </div>

          <p className="text-center text-plum-500 font-medium mt-10 max-w-2xl mx-auto" data-id="element-370">
            All sessions are personalized to your needs. Once you submit your
            request, I'll reach out to schedule and confirm details.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center px-4" data-id="element-371">
        <div className="max-w-2xl mx-auto bg-white p-12 rounded-[3rem] shadow-xl border border-blush-100 relative overflow-hidden" data-id="element-372">
          <SparkleEffect count={50} data-id="element-373" />
          <div className="relative z-10" data-id="element-374">
            <h2 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-375">
              Ready to invest in yourself?
            </h2>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-376">
              Real-life guidance, mindset support, and confidence rebuilding.
              Let's figure out the right fit for you.
            </p>
            <button onClick={() => handleRequestSession('General Inquiry')} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-lg hover:-translate-y-1" data-id="element-377">
              <GlitterHeart size={20} variant="light" data-id="element-378" />
              Start Your Transformation
            </button>
          </div>
        </div>
      </section>
    </div>;
}